<script lang="ts" setup>
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue'
import IconSearch from '~/assets/icon/general/Search.svg'
import NavigationArrowRight from '~/assets/icon/navigation/Arrow/Right.svg'
import IconCross from '~/assets/icon/general/Cross.svg'
import IconCheck from '~/assets/icon/general/Check.svg'
import { normalize } from '../../utils/normalize'
const { locale } = useI18n()

const props = defineProps({
  autocomplete: {
    type: Boolean,
    required: true,
  },
  sidney: {
    type: Boolean,
    required: false,
    default: false,
  },
  inHome: {
    type: Boolean,
    required: false,
    default: false,
  },
})

let searchState = useState('searchState')

const autocompleteLoading = ref<boolean>(false)
const selectedResult = ref(null)
const searchInput = ref(null)
const route = useRoute()
const collection = ref('global')
watch(
  () => route.query.collection,
  (newValue) => {
    collection.value = newValue ?? 'global'
  }
)

const $emit = defineEmits<{
  (e: 'search', value: string): void
}>()
const data = ref(null)
const pending = ref(null)
const error = ref(null)

watch([() => searchState.value], async () => {
  const _query = {
    q: searchState.value,
    fields: ['title', 'subtitle', 'description'].join('-'),
    include: [
      `title, titleEn,subtitle,type,slug${props.sidney ? ',description' : ''}`,
    ],
    limit: 6,
  }
  if (!props.sidney) {
    _query.sort_by = 'typeWeight:asc'
  }
  const result = await useAsyncData('autocomplete', () =>
    execSearch(
      props.sidney || collection.value === 'work_versions'
        ? 'work_versions'
        : 'global',
      _query
    )
  )
  data.value = result.data.value
})
const search = computed(() => {
  if (searchState.value) {
    return searchState.value
  } else if (selectedResult.value) {
    return selectedResult.value
  } else return ''
})

function formatResult(doc) {
  let value = ''
  if (props.sidney) {
    value += doc?.document?.description ? `${doc?.document?.description}, ` : ''
  }
  if (doc?.document?.type === 'work') {
    value += doc?.document?.subtitle ? `${doc?.document?.subtitle}, ` : ''
  }
  value +=
    locale.value === 'en' && doc.document.titleEn
      ? doc.document.titleEn
      : doc.document.title
  if (props.sidney) {
    value += doc?.document?.subtitle ? `, ${doc?.document?.subtitle}` : ''
  }

  return value
}
const fetchResults = computed<Collection[]>(() => {
  if (data.value === null || pending.value || error.value) return []
  const results = []
  for (const doc of data.value.documents) {
    const value = formatResult(doc)
    results.push(value)
  }
  return results
})

const autocompleteResults = computed(() =>
  searchState.value === ''
    ? fetchResults.value
    : fetchResults.value.filter((result) => {
        const normalizedSearch = normalize(searchState.value.toLowerCase())
        const normalizedResult = normalize(result.toLowerCase())
        return normalizedResult.includes(normalizedSearch)
      })
)

const getRndInteger = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}
watchThrottled(
  searchState,
  (newQuery) => {
    autocompleteLoading.value = true
    setTimeout(() => {
      autocompleteLoading.value = false
    }, getRndInteger(500, 2000))
  },
  { throttle: 500 }
)
watch(selectedResult, (newValue) => {
  if (newValue) {
    searchState.value = newValue
  }
})

const { goToResource } = useGoToResource()
const getResult = (result) => {
  const value = data.value.documents.find((doc) => formatResult(doc) === result)

  return value?.document
}
const getSelected = (selected: string) => {
  selectedResult.value = selected
  const value = getResult(selected)
  if (props.sidney) {
    value.type = 'sidney'
  }
  goToResource(value)
}
const clearSearchInput = () => {
  selectedResult.value = null
  searchState.value = ''
}
function onChange($event) {
  searchState.value = $event.target.value
  $emit('search', $event.target.value)
}
</script>
<template>
  <div class="group relative font-normal">
    <div class="absolute inset-y-0 left-4 flex items-center">
      <IconSearch
        class="hidden h-6 w-6 transition-colors duration-200 ease-in-out group-hover:text-primary-400 md:block"
      />
    </div>
    <Combobox v-if="autocomplete" v-model="search">
      <ComboboxInput
        ref="searchInput"
        :class="{
          'h-full w-full': inHome,
          'h-4 w-40 md:h-full md:w-full': !inHome,
        }"
        class="text-xs placeholder:text-md border border-shade-900 py-4 transition-colors placeholder:capitalize placeholder:text-shade-alpha-900 hover:border-primary-400 focus:border-primary-400 focus:ring focus:ring-primary-alpha-400 focus-visible:outline-none md:pl-14 md:pr-20 md:text-lg"
        v-bind="{ ...$props, ...$attrs }"
        autocomplete="off"
        @change="onChange($event)"
      />
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 -translate-y-4 opacity-0"
        enter-to-class="transform scale-100 translate-y-0 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 translate-y-0 opacity-100"
        leave-to-class="transform scale-95 -translate-y-4 opacity-0"
      >
        <ComboboxOptions
          class="absolute z-20 mt-1 flex w-full flex-col border border-shade-900 bg-shade-50 p-3 shadow-sm"
        >
          <div
            v-if="autocompleteLoading"
            class="relative cursor-default select-none text-shade-600 first-letter:uppercase"
          >
            {{ $t('labels.loading') }}
          </div>
          <div
            v-if="autocompleteResults.length === 0 && searchState !== ''"
            class="relative cursor-default select-none text-shade-600 first-letter:uppercase"
          >
            {{ $t('labels.nothing_found') }}
          </div>
          <ComboboxOption
            v-for="result in autocompleteResults"
            :key="result"
            v-slot="{ selected, active }"
            :value="result"
            as="template"
            @click="getSelected(result)"
          >
            <li
              class="relative cursor-pointer select-none py-2.5 md:pl-8 md:pr-6"
              :class="{
                'bg-primary-alpha-100 text-primary-400': selected,
                'bg-shade-alpha-300': active && !selected,
              }"
            >
              <span
                class="block truncate"
                :class="{ 'font-medium': selected, 'font-normal': !selected }"
              >
                {{ result }}
              </span>
              <span
                v-if="
                  ![
                    'composer',
                    'author',
                    'composer-video',
                    'composer-audio',
                  ].includes(getResult(result)?.type)
                "
                class="inline-block cursor-pointer border border-shade-200 bg-white px-1 transition-colors duration-150 ease-out hover:bg-shade-900 hover:text-white"
                >{{
                  $t(
                    `resource.type.${
                      sidney ? 'sidney' : getResult(result)?.type
                    }`
                  )
                }}</span
              >
              <span
                v-if="selected"
                class="absolute inset-y-0 left-0 flex items-center pl-2"
                :class="{
                  'text-primary-400': selected,
                  'text-shade-900': active && !selected,
                }"
              >
                <IconCheck class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </transition>
    </Combobox>
    <input
      v-else
      v-model="value"
      type="text"
      class="placeholder:text-md border border-shade-900 py-4 pl-14 pr-20 text-lg transition-colors placeholder:capitalize placeholder:text-shade-alpha-900 hover:border-primary-400 focus:border-primary-400 focus:ring focus:ring-primary-alpha-400 focus-visible:outline-none"
    />
    <div class="absolute inset-y-0 right-4 flex items-center gap-3">
      <button
        v-show="searchState !== null"
        v-if="autocomplete"
        class="flex hidden h-4 w-4 items-center justify-center rounded-full bg-shade-200 transition-colors duration-200 ease-in-out hover:bg-shade-900 md:block"
        type="reset"
        @click.prevent="clearSearchInput"
      >
        <IconCross class="h-3 w-3 text-shade-50" />
      </button>
      <div class="flex items-center">
        <button
          class="flex h-8 w-8 items-center justify-center bg-primary-400 text-white transition-colors duration-200 ease-in-out hover:bg-shade-900 focus:ring focus:ring-primary-alpha-400 focus-visible:outline-none disabled:bg-primary-100"
          type="submit"
          @click="$emit('search', searchState)"
        >
          <NavigationArrowRight class="h-5 w-5" />
        </button>
      </div>
    </div>
  </div>
</template>
